import React, { useState, useEffect } from "react";
import { useSmartNavigation } from "app/utils/navigationHelper";
import { useAPI } from "app/hooks/useApi";
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  IconButton,
  Box,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  Divider,
  TablePagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Snackbar,
  FormControlLabel,
  Checkbox,
  TextField,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import {
  ChevronLeft,
  ChevronRight,
  Today as TodayIcon,
  Event as EventIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  Label as LabelIcon,
  ExpandMore as ExpandMoreIcon,
  FilterList as FilterListIcon,
  Clear as ClearIcon,
} from "@mui/icons-material";
import JumboContentLayout from "@jumbo/components/JumboContentLayout";
import moment from "moment";
import "moment/locale/pt";

moment.locale("pt");

// ─── Helpers ────────────────────────────────────────────────────────────────

const mapStatusFromApi = (apiStatus) => {
  const statusMap = {
    ABERTO: "pendente",
    EM_ANDAMENTO: "em_andamento",
    AGENDADO: "agendado",
    CONFIRMADO: "confirmado",
    CONCLUIDO: "concluido",
  };
  return statusMap[apiStatus] || "pendente";
};

const mapEventType = (prazoInfo) => {
  if (prazoInfo.prazo_anos && prazoInfo.prazo_anos >= 1) return "vencimento";
  return "prazo";
};

const determinePriority = (prazoInfo) => {
  const diasRestantes = moment(prazoInfo.data_limite).diff(moment(), "days");
  if (diasRestantes <= 7) return "alta";
  if (diasRestantes <= 30) return "media";
  return "baixa";
};

const eventTypeColors = {
  audiencia: "#1976d2",
  vencimento: "#d32f2f",
  reuniao: "#388e3c",
  prazo: "#F7DD72",
  acompanhamento: "#7b1fa2",
};

const priorityColors = {
  alta: "#d32f2f",
  media: "#f57c00",
  baixa: "#388e3c",
};

const statusLabels = {
  confirmado: "Confirmado",
  pendente: "Pendente",
  em_andamento: "Em Andamento",
  agendado: "Agendado",
  concluido: "Concluído",
};

const statusColors = {
  confirmado: "#388e3c",
  pendente: "#f57c00",
  em_andamento: "#1976d2",
  agendado: "#7b1fa2",
  concluido: "#616161",
};

// ─── Componente principal ────────────────────────────────────────────────────

const Agenda = () => {
  const { navigateToProcesso } = useSmartNavigation();
  const { makeRequest, loading } = useAPI();

  const [currentDate, setCurrentDate] = useState(moment());
  // Mês que foi de fato buscado na API (pode diferir de currentDate enquanto o usuário navega)
  const [fetchedMonth, setFetchedMonth] = useState(moment());
  const [events, setEvents] = useState([]);
  const [totalEvents, setTotalEvents] = useState(0);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [editedPriority, setEditedPriority] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [apenasVivian, setApenasVivian] = useState(true);

  // ── Estado dos filtros avançados (apenas controla os inputs — não dispara fetch)
  const FILTERS_INITIAL = {
    ordenacao: "data_limite",
    status: "",
    prazo: "",
    titular: "",
    responsavel: "",
    processo: "",
    orgao: "",
    tipoProvidencia: "",
    dataInicio: "",
    dataFim: "",
  };
  const [filters, setFilters] = useState(FILTERS_INITIAL);

  // appliedFilters são os filtros que foram efetivamente enviados à API (via "Aplicar")
  const [appliedFilters, setAppliedFilters] = useState(FILTERS_INITIAL);

  // Indica se o usuário editou dataFim manualmente (para não sobrescrever com o auto-cálculo)
  const [dataFimManual, setDataFimManual] = useState(false);

  // Indica se há alterações pendentes que ainda não foram aplicadas
  const hasPendingChanges = JSON.stringify(filters) !== JSON.stringify(appliedFilters);

  const [filtersExpanded, setFiltersExpanded] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "info" });
  const [dayEventsDialog, setDayEventsDialog] = useState({ open: false, date: null, events: [] });

  // Sinaliza que o calendário está em mês diferente do carregado (sem filtro de data manual)
  const monthOutOfSync =
    !currentDate.isSame(fetchedMonth, "month") &&
    !appliedFilters.dataInicio &&
    !appliedFilters.dataFim;

  // ── Fetch — dispara apenas com appliedFilters, paginação, fetchedMonth e apenasVivian
  useEffect(() => {
    const dataInicio =
      appliedFilters.dataInicio || fetchedMonth.clone().startOf("month").format("YYYY-MM-DD");
    const dataFim =
      appliedFilters.dataFim ||
      (appliedFilters.dataInicio
        ? moment(appliedFilters.dataInicio).endOf("month").format("YYYY-MM-DD")
        : fetchedMonth.clone().endOf("month").format("YYYY-MM-DD"));
    fetchAgendaData(dataInicio, dataFim);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    page,
    rowsPerPage,
    apenasVivian,
    fetchedMonth.format("YYYY-MM"),
    appliedFilters.ordenacao,
    appliedFilters.status,
    appliedFilters.prazo,
    appliedFilters.titular,
    appliedFilters.responsavel,
    appliedFilters.processo,
    appliedFilters.orgao,
    appliedFilters.tipoProvidencia,
    appliedFilters.dataInicio,
    appliedFilters.dataFim,
  ]);

  const fetchAgendaData = async (dataInicio, dataFim) => {
    try {
      setSnackbar({ open: true, message: "Buscando dados da agenda...", severity: "info" });

      const queryParams = new URLSearchParams({
        page: page + 1,
        limit: rowsPerPage,
        sort: appliedFilters.ordenacao || "data_limite",
        order: "asc",
        apenasVivian: apenasVivian.toString(),
        dataInicio,
        dataFim,
      });

      if (appliedFilters.status) queryParams.append("status", appliedFilters.status);
      if (appliedFilters.prazo) queryParams.append("prazo", appliedFilters.prazo);
      if (appliedFilters.titular) queryParams.append("titular", appliedFilters.titular);
      if (appliedFilters.responsavel) queryParams.append("responsavel", appliedFilters.responsavel);
      if (appliedFilters.processo) queryParams.append("processo", appliedFilters.processo);
      if (appliedFilters.orgao) queryParams.append("orgao", appliedFilters.orgao);
      if (appliedFilters.tipoProvidencia)
        queryParams.append("tipoProvidencia", appliedFilters.tipoProvidencia);

      const response = await makeRequest(
        `/agenda?${queryParams.toString()}`,
        "GET",
        null,
        {},
        true
      );

      if (response?.status === 200 && response?.data?.items) {
        const formattedEvents = formatEventsFromApi(response.data.items);
        const total = response.data.meta?.total || formattedEvents.length;

        setEvents(formattedEvents);
        setTotalEvents(total);

        const filtroTexto = apenasVivian ? " (Apenas Vivian)" : " (Todos os processos)";
        setSnackbar({
          open: true,
          message: `${formattedEvents.length} eventos carregados de ${total} total${filtroTexto}.`,
          severity: "success",
        });
      } else {
        setEvents([]);
        setTotalEvents(0);
        setSnackbar({
          open: true,
          message: "Não foi possível carregar os eventos da agenda.",
          severity: "warning",
        });
      }
    } catch (error) {
      setEvents([]);
      setTotalEvents(0);
      setSnackbar({
        open: true,
        message: `Erro ao acessar a API: ${error.message || "Erro desconhecido"}`,
        severity: "error",
      });
    }
  };

  const formatEventsFromApi = (apiEvents) => {
    if (!Array.isArray(apiEvents)) return [];
    return apiEvents
      .map((item) => {
        try {
          return {
            id: item.id,
            processoId: item.processo_rpi_id,
            title: `Código ${item.despacho_codigo || "N/A"} - ${item.descricao || "Sem descrição"}`,
            description: item.descricao || "Sem descrição",
            date: item.data_limite || new Date(),
            time: moment(item.data_limite || new Date()).format("HH:mm"),
            type: mapEventType(item),
            priority: determinePriority(item),
            client: item.processo_rpi?.cliente?.nome || "Cliente não informado",
            responsible:
              item.processo_rpi?.procurador && item.processo_rpi.procurador !== "undefined"
                ? item.processo_rpi.procurador
                : "Não informado",
            location: "INPI",
            status: mapStatusFromApi(item.status || "ABERTO"),
            codigo: item.despacho_codigo || "N/A",
            numero: item.processo_rpi?.numero || "N/A",
            marca: item.processo_rpi?.marca?.[0]?.nome || "Marca não informada",
            significadoCodigo: item.descricao || "Sem descrição",
            despacho: item.descricao || "Sem descrição",
            prazo: item.prazo_dias
              ? `${item.prazo_dias} dias`
              : item.prazo_anos
              ? `${item.prazo_anos} anos`
              : "Prazo não definido",
            dataPublicacao: item.data_inicio || new Date(),
            prazoVencimento: item.data_limite || new Date(),
          };
        } catch {
          return null;
        }
      })
      .filter(Boolean);
  };

  const concluirPrazo = async (id) => {
    try {
      const response = await makeRequest(`/agenda/${id}/concluir`, "PATCH", null, {}, true);
      if (response?.status === 200) {
        setEvents((prev) => prev.map((e) => (e.id === id ? { ...e, status: "concluido" } : e)));
        setSnackbar({ open: true, message: "Prazo concluído com sucesso!", severity: "success" });
        return true;
      }
      setSnackbar({
        open: true,
        message: "Não foi possível concluir o prazo.",
        severity: "warning",
      });
      return false;
    } catch (error) {
      setSnackbar({
        open: true,
        message: `Erro ao concluir prazo: ${error.message || "Erro desconhecido"}`,
        severity: "error",
      });
      return false;
    }
  };

  // ── Ordenação local (fallback) ─────────────────────────────────────────────
  const priorityOrder = { alta: 0, media: 1, baixa: 2 };

  const sortedEvents = [...events].sort((a, b) => {
    if (appliedFilters.ordenacao === "data_limite" || !appliedFilters.ordenacao) {
      const diff = moment(a.date).diff(moment(b.date));
      return diff !== 0 ? diff : priorityOrder[a.priority] - priorityOrder[b.priority];
    }
    return 0;
  });

  // ── Handlers de navegação do calendário ─────────────────────────────────────
  // Apenas movem o calendário visual — NÃO disparam fetch
  const handlePrevMonth = () => setCurrentDate((d) => d.clone().subtract(1, "month"));
  const handleNextMonth = () => setCurrentDate((d) => d.clone().add(1, "month"));
  const handleToday = () => setCurrentDate(moment());

  // Atualiza o mês buscado na API para o mês do calendário e dispara fetch
  const handleAtualizar = () => {
    const novoMes = currentDate.clone();
    setFetchedMonth(novoMes);
    setPage(0);
    const dataInicio =
      appliedFilters.dataInicio || novoMes.clone().startOf("month").format("YYYY-MM-DD");
    const dataFim =
      appliedFilters.dataFim ||
      (appliedFilters.dataInicio
        ? moment(appliedFilters.dataInicio).endOf("month").format("YYYY-MM-DD")
        : novoMes.clone().endOf("month").format("YYYY-MM-DD"));
    fetchAgendaData(dataInicio, dataFim);
  };

  // ── Handlers de paginação ─────────────────────────────────────────────────
  const handlePageChange = (_, newPage) => setPage(newPage);
  const handleRowsPerPageChange = (e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  // ── Handlers de evento ────────────────────────────────────────────────────
  const handleEventClick = (event) => {
    setSelectedEvent(event);
    setEditedPriority(event.priority);
    setOpenDialog(true);
  };

  const handleSaveChanges = async () => {
    const updatedEvents = events.map((e) =>
      e.id === selectedEvent.id ? { ...e, priority: editedPriority } : e
    );
    if (selectedEvent.status !== "concluido") {
      const success = await concluirPrazo(selectedEvent.id);
      if (success) setEvents(updatedEvents);
    } else {
      setEvents(updatedEvents);
    }
    setOpenDialog(false);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditedPriority("");
  };

  const handleCloseSnackbar = () => setSnackbar((p) => ({ ...p, open: false }));

  const handleShowDayEvents = (day) => {
    setDayEventsDialog({ open: true, date: day, events: getEventsForDay(day) });
  };

  const handleCloseDayEventsDialog = () =>
    setDayEventsDialog({ open: false, date: null, events: [] });

  const handleApenasVivianChange = (e) => {
    setFetchedMonth(currentDate.clone());
    setApenasVivian(e.target.checked);
    setPage(0);
    // Garante que filters e appliedFilters estejam sincronizados
    setAppliedFilters((prev) => ({ ...prev }));
  };

  // ── Handlers de filtro ────────────────────────────────────────────────────
  // Apenas atualiza o estado local dos inputs — NÃO dispara fetch
  const handleFilterChange = (name, value) => {
    if (name === "dataInicio") {
      if (value) {
        const mesInicio = moment(value);
        setCurrentDate(mesInicio.clone());
        setFilters((p) => ({
          ...p,
          dataInicio: value,
          // Recalcula dataFim como fim do mês, a menos que o usuário tenha editado manualmente
          dataFim: dataFimManual
            ? p.dataFim
            : mesInicio.clone().endOf("month").format("YYYY-MM-DD"),
        }));
      } else {
        setFilters((p) => ({ ...p, dataInicio: value, dataFim: dataFimManual ? p.dataFim : "" }));
      }
      return;
    }
    if (name === "dataFim") {
      setDataFimManual(!!value); // marca como manual se preenchido, desmarca se limpo
    }
    setFilters((p) => ({ ...p, [name]: value }));
  };

  // Aplica os filtros e dispara o fetch
  const handleAplicarFiltros = () => {
    const novosFilters = { ...filters };
    setAppliedFilters(novosFilters);
    // Move fetchedMonth para o mês da dataInicio (se preenchida) ou mantém currentDate
    const novoMes = novosFilters.dataInicio ? moment(novosFilters.dataInicio) : currentDate.clone();
    setFetchedMonth(novoMes.clone());
    setPage(0);
  };

  const handleClearFilters = () => {
    setFilters(FILTERS_INITIAL);
    setAppliedFilters(FILTERS_INITIAL);
    setDataFimManual(false);
    setFetchedMonth(currentDate.clone());
    setPage(0);
  };

  // "tem filtro ativo" = filtros que foram aplicados (não apenas digitados)
  const hasActiveFilters = () =>
    Object.entries(appliedFilters).some(([k, v]) => k !== "ordenacao" && v !== "");

  const getActiveFiltersCount = () =>
    Object.entries(appliedFilters).filter(([k, v]) => k !== "ordenacao" && v !== "").length;

  // ── Calendário ────────────────────────────────────────────────────────────
  const generateCalendarDays = () => {
    const start = currentDate.clone().startOf("month").startOf("week");
    const end = currentDate.clone().endOf("month").endOf("week");
    const days = [];
    const cur = start.clone();
    while (cur.isSameOrBefore(end, "day")) {
      days.push(cur.clone());
      cur.add(1, "day");
    }
    return days;
  };

  const getEventsForDay = (day) => events.filter((e) => moment(e.date).isSame(day, "day"));
  const calendarDays = generateCalendarDays();

  // ── Loading inicial ───────────────────────────────────────────────────────
  if (loading && events.length === 0) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "80vh" }}>
        <CircularProgress />
      </Box>
    );
  }

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <JumboContentLayout>
      <Box sx={{ p: 3 }}>
        {/* Header */}
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
          <Typography variant="h4" component="h1" fontWeight="bold">
            Agenda de Marcas
          </Typography>
          <Button variant="contained" color="primary" onClick={handleAtualizar} disabled={loading}>
            {loading
              ? "Carregando..."
              : monthOutOfSync
              ? `Carregar ${currentDate.format("MMMM YYYY")}`
              : "Atualizar"}
          </Button>
        </Box>

        {/* Calendário */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box
              sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <IconButton onClick={handlePrevMonth}>
                  <ChevronLeft />
                </IconButton>
                <Typography variant="h5" sx={{ minWidth: 200, textAlign: "center" }}>
                  {currentDate.format("MMMM YYYY")}
                </Typography>
                <IconButton onClick={handleNextMonth}>
                  <ChevronRight />
                </IconButton>
              </Box>
              <Button variant="outlined" startIcon={<TodayIcon />} onClick={handleToday}>
                Hoje
              </Button>
            </Box>

            <Grid container spacing={1}>
              {["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"].map((day) => (
                <Grid item xs={12 / 7} key={day}>
                  <Typography
                    variant="subtitle2"
                    textAlign="center"
                    fontWeight="bold"
                    color="text.secondary"
                    sx={{ p: 1 }}
                  >
                    {day}
                  </Typography>
                </Grid>
              ))}

              {calendarDays.map((day) => {
                const dayEvents = getEventsForDay(day);
                const isCurrentMonth = day.isSame(currentDate, "month");
                const isToday = day.isSame(moment(), "day");
                return (
                  <Grid item xs={12 / 7} key={day.format("YYYY-MM-DD")}>
                    <Box
                      sx={{
                        minHeight: 100,
                        border: "1px solid #e0e0e0",
                        borderRadius: 1,
                        p: 1,
                        bgcolor: isToday ? "#e3f2fd" : "transparent",
                        opacity: isCurrentMonth ? 1 : 0.3,
                      }}
                    >
                      <Typography
                        variant="body2"
                        fontWeight={isToday ? "bold" : "normal"}
                        color={isToday ? "primary.main" : "text.primary"}
                      >
                        {day.format("D")}
                      </Typography>
                      {dayEvents.slice(0, 2).map((event) => (
                        <Chip
                          key={event.id}
                          label={`${moment(event.date).format("DD/MM")} - ${event.numero} - ${
                            event.codigo
                          }`}
                          size="small"
                          onClick={() => handleEventClick(event)}
                          sx={{
                            mt: 0.5,
                            width: "100%",
                            fontSize: "0.7rem",
                            bgcolor: eventTypeColors[event.type],
                            color: "white",
                            cursor: "pointer",
                            "&:hover": { opacity: 0.8 },
                          }}
                        />
                      ))}
                      {dayEvents.length > 2 && (
                        <Typography
                          variant="caption"
                          color="primary.main"
                          sx={{
                            mt: 0.5,
                            display: "block",
                            cursor: "pointer",
                            textDecoration: "underline",
                            "&:hover": { color: "primary.dark", fontWeight: "bold" },
                          }}
                          onClick={() => handleShowDayEvents(day)}
                        >
                          +{dayEvents.length - 2} mais
                        </Typography>
                      )}
                    </Box>
                  </Grid>
                );
              })}
            </Grid>
          </CardContent>
        </Card>

        {/* Lista de eventos */}
        <Card>
          <CardContent>
            <Box
              sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}
            >
              <Typography variant="h6">
                Prazos INPI de {currentDate.format("MMMM YYYY")}
                {(filters.dataInicio || filters.dataFim) && (
                  <Typography
                    component="span"
                    variant="caption"
                    color="text.secondary"
                    sx={{ ml: 1 }}
                  >
                    (filtro de data personalizado)
                  </Typography>
                )}
              </Typography>

              <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
                <Button
                  startIcon={<FilterListIcon />}
                  onClick={() => setFiltersExpanded((p) => !p)}
                  variant={hasActiveFilters() ? "contained" : "outlined"}
                  size="small"
                  color={hasActiveFilters() ? "primary" : "inherit"}
                >
                  Filtros Avançados
                  {hasActiveFilters() && (
                    <Chip
                      label={getActiveFiltersCount()}
                      size="small"
                      sx={{ ml: 1, bgcolor: "white", color: "primary.main" }}
                    />
                  )}
                </Button>

                <FormControlLabel
                  control={
                    <Checkbox
                      checked={apenasVivian}
                      onChange={handleApenasVivianChange}
                      color="primary"
                    />
                  }
                  label="Apenas Vivian"
                  sx={{
                    mr: 2,
                    "& .MuiFormControlLabel-label": {
                      fontSize: "0.875rem",
                      fontWeight: apenasVivian ? "bold" : "normal",
                      color: apenasVivian ? "primary.main" : "text.secondary",
                    },
                  }}
                />

                <Typography variant="body2" color="text.secondary">
                  {totalEvents > 0
                    ? `Mostrando ${page * rowsPerPage + 1}–${Math.min(
                        (page + 1) * rowsPerPage,
                        totalEvents
                      )} de ${totalEvents} evento(s)${apenasVivian ? " (Vivian)" : " (Todos)"}${
                        hasActiveFilters() ? " (Filtrado)" : ""
                      }`
                    : "Nenhum evento encontrado"}
                </Typography>
              </Box>
            </Box>

            {/* ✅ FIX 2: Filtros avançados com debounce nos campos de texto/data */}
            <Accordion
              expanded={filtersExpanded}
              onChange={() => setFiltersExpanded((p) => !p)}
              sx={{
                mt: 2,
                "&:before": { display: "none" },
                boxShadow: hasActiveFilters() ? 2 : 1,
                border: hasActiveFilters() ? "2px solid" : "1px solid",
                borderColor: hasActiveFilters() ? "primary.main" : "divider",
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{ bgcolor: "#f5f5f5", "&:hover": { bgcolor: "#eeeeee" } }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <FilterListIcon />
                  <Typography variant="subtitle1">Filtros Avançados</Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Paper elevation={0} sx={{ p: 2, bgcolor: "#fafafa" }}>
                  <Grid container spacing={2}>
                    {/* Linha 1 */}
                    <Grid item xs={12} sm={6} md={3}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Ordenação</InputLabel>
                        <Select
                          value={filters.ordenacao}
                          label="Ordenação"
                          onChange={(e) => handleFilterChange("ordenacao", e.target.value)}
                        >
                          <MenuItem value="data_limite">Data Limite</MenuItem>
                          <MenuItem value="createdAt">Data Criação</MenuItem>
                          <MenuItem value="numero_processo">Número Processo</MenuItem>
                          <MenuItem value="titular">Titular</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>

                    <Grid item xs={12} sm={6} md={3}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Status</InputLabel>
                        <Select
                          value={filters.status}
                          label="Status"
                          onChange={(e) => handleFilterChange("status", e.target.value)}
                        >
                          <MenuItem value="">Todos</MenuItem>
                          <MenuItem value="ABERTO">Aberto</MenuItem>
                          <MenuItem value="EM_ANDAMENTO">Em Andamento</MenuItem>
                          <MenuItem value="AGENDADO">Agendado</MenuItem>
                          <MenuItem value="CONFIRMADO">Confirmado</MenuItem>
                          <MenuItem value="CONCLUIDO">Concluído</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>

                    <Grid item xs={12} sm={6} md={3}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Prazo</InputLabel>
                        <Select
                          value={filters.prazo}
                          label="Prazo"
                          onChange={(e) => handleFilterChange("prazo", e.target.value)}
                        >
                          <MenuItem value="">Todos</MenuItem>
                          <MenuItem value="vencidos">Vencidos</MenuItem>
                          <MenuItem value="hoje">Hoje</MenuItem>
                          <MenuItem value="7_dias">Próximos 7 dias</MenuItem>
                          <MenuItem value="30_dias">Próximos 30 dias</MenuItem>
                          <MenuItem value="90_dias">Próximos 90 dias</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>

                    {/* ✅ TextField com debounce — não dispara requisição a cada tecla */}
                    <Grid item xs={12} sm={6} md={3}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Titular"
                        placeholder="Buscar por titular..."
                        value={filters.titular}
                        onChange={(e) => handleFilterChange("titular", e.target.value)}
                      />
                    </Grid>

                    {/* Linha 2 */}
                    <Grid item xs={12} sm={6} md={3}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Responsável</InputLabel>
                        <Select
                          value={filters.responsavel}
                          label="Responsável"
                          onChange={(e) => handleFilterChange("responsavel", e.target.value)}
                        >
                          <MenuItem value="">Todos</MenuItem>
                          <MenuItem value="vivian">Vivian</MenuItem>
                          <MenuItem value="admin">Admin</MenuItem>
                          <MenuItem value="operador">Operador</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>

                    <Grid item xs={12} sm={6} md={3}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Processo"
                        placeholder="Buscar por número..."
                        value={filters.processo}
                        onChange={(e) => handleFilterChange("processo", e.target.value)}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6} md={3}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Órgão</InputLabel>
                        <Select
                          value={filters.orgao}
                          label="Órgão"
                          onChange={(e) => handleFilterChange("orgao", e.target.value)}
                        >
                          <MenuItem value="">Todos</MenuItem>
                          <MenuItem value="INPI">INPI</MenuItem>
                          <MenuItem value="DIRPA">DIRPA</MenuItem>
                          <MenuItem value="CGPAT">CGPAT</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>

                    <Grid item xs={12} sm={6} md={3}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Tipo de Providência</InputLabel>
                        <Select
                          value={filters.tipoProvidencia}
                          label="Tipo de Providência"
                          onChange={(e) => handleFilterChange("tipoProvidencia", e.target.value)}
                        >
                          <MenuItem value="">Todos</MenuItem>
                          <MenuItem value="peticionamento">Peticionamento</MenuItem>
                          <MenuItem value="pagamento">Pagamento</MenuItem>
                          <MenuItem value="acompanhamento">Acompanhamento</MenuItem>
                          <MenuItem value="renovacao">Renovação</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>

                    {/* Linha 3 — datas com debounce */}
                    <Grid item xs={12} sm={6} md={3}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Data Início"
                        type="date"
                        value={filters.dataInicio}
                        onChange={(e) => handleFilterChange("dataInicio", e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        helperText={
                          filters.dataInicio
                            ? "Calendário movido para este mês"
                            : "Padrão: mês do calendário"
                        }
                      />
                    </Grid>

                    <Grid item xs={12} sm={6} md={3}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Data Fim (opcional)"
                        type="date"
                        value={filters.dataFim}
                        onChange={(e) => handleFilterChange("dataFim", e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        helperText={
                          filters.dataFim
                            ? "Intervalo personalizado"
                            : filters.dataInicio
                            ? `Padrão: ${moment(filters.dataInicio)
                                .endOf("month")
                                .format("DD/MM/YYYY")}`
                            : "Padrão: fim do mês do calendário"
                        }
                      />
                    </Grid>

                    {/* Ações */}
                    <Grid item xs={12}>
                      <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={handleAplicarFiltros}
                          size="small"
                          disabled={loading}
                        >
                          {hasPendingChanges ? "Aplicar filtros *" : "Aplicar filtros"}
                        </Button>
                        <Button
                          variant="outlined"
                          startIcon={<ClearIcon />}
                          onClick={handleClearFilters}
                          size="small"
                        >
                          Limpar
                        </Button>
                        {hasPendingChanges && (
                          <Typography
                            variant="caption"
                            color="warning.main"
                            sx={{ fontStyle: "italic" }}
                          >
                            Há alterações não aplicadas
                          </Typography>
                        )}
                        {!hasPendingChanges && hasActiveFilters() && (
                          <Typography variant="caption" color="text.secondary">
                            {getActiveFiltersCount()} filtro(s) ativo(s)
                          </Typography>
                        )}
                      </Box>
                    </Grid>
                  </Grid>
                </Paper>
              </AccordionDetails>
            </Accordion>

            {/* Lista */}
            <Stack spacing={2} sx={{ mt: 2 }}>
              {sortedEvents.length === 0 ? (
                <Typography color="text.secondary" textAlign="center" sx={{ py: 4 }}>
                  Nenhum evento agendado para {currentDate.format("MMMM YYYY")}
                </Typography>
              ) : (
                sortedEvents.map((event) => (
                  <Card
                    key={event.id}
                    variant="outlined"
                    sx={{
                      cursor: "pointer",
                      borderLeft: `4px solid ${eventTypeColors[event.type]}`,
                      "&:hover": { bgcolor: "#f5f5f5" },
                    }}
                    onClick={() => handleEventClick(event)}
                  >
                    <CardContent>
                      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <Typography variant="h6" component="div">
                            {moment(event.date).format("DD/MM/YYYY")} -
                          </Typography>
                          <Typography
                            variant="h6"
                            component="span"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigateToProcesso(event.processoId);
                            }}
                            sx={{
                              color: "primary.main",
                              textDecoration: "underline",
                              fontWeight: "bold",
                              cursor: "pointer",
                              "&:hover": { color: "primary.dark" },
                            }}
                          >
                            {event.numero}
                          </Typography>
                          <Typography variant="h6" component="div">
                            - {event.codigo}
                          </Typography>
                        </Box>
                        <Box sx={{ display: "flex", gap: 1 }}>
                          <Chip
                            label={event.priority.toUpperCase()}
                            size="small"
                            sx={{ bgcolor: priorityColors[event.priority], color: "white" }}
                          />
                          <Chip
                            label={statusLabels[event.status]}
                            size="small"
                            sx={{ bgcolor: statusColors[event.status], color: "white" }}
                          />
                        </Box>
                      </Box>
                      <Typography color="text.secondary" gutterBottom>
                        {event.description}
                      </Typography>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 1 }}>
                        <LabelIcon fontSize="small" color="primary" />
                        <Typography variant="body2" fontWeight="bold" color="primary.main">
                          Marca: {event.marca}
                        </Typography>
                      </Box>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 2, fontStyle: "italic" }}
                      >
                        {event.significadoCodigo}
                      </Typography>
                      <Stack direction="row" spacing={2} alignItems="center">
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                          <EventIcon fontSize="small" color="action" />
                          <Typography variant="body2">
                            {moment(event.date).format("DD/MM/YYYY")}
                          </Typography>
                        </Box>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                          <LabelIcon fontSize="small" color="action" />
                          <Typography variant="body2">{event.marca}</Typography>
                        </Box>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                          <PersonIcon fontSize="small" color="action" />
                          <Typography variant="body2">{event.responsible}</Typography>
                        </Box>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                          <BusinessIcon fontSize="small" color="action" />
                          <Typography variant="body2">{event.client}</Typography>
                        </Box>
                      </Stack>
                    </CardContent>
                  </Card>
                ))
              )}
            </Stack>

            {/* Paginação */}
            {totalEvents > 0 && (
              <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
                <TablePagination
                  component="div"
                  count={totalEvents}
                  page={page}
                  onPageChange={handlePageChange}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={handleRowsPerPageChange}
                  rowsPerPageOptions={[5, 10, 15, 20, 25]}
                  labelRowsPerPage="Eventos por página:"
                  labelDisplayedRows={({ from, to, count }) =>
                    `${from}-${to} de ${count !== -1 ? count : `mais de ${to}`}`
                  }
                />
              </Box>
            )}
          </CardContent>
        </Card>

        {/* Dialog — Detalhes do prazo */}
        <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
          <DialogTitle>Detalhes do Prazo INPI</DialogTitle>
          <DialogContent>
            {selectedEvent && (
              <Stack spacing={2} sx={{ mt: 1 }}>
                <Typography variant="h6">
                  {moment(selectedEvent.date).format("DD/MM/YYYY")} - {selectedEvent.numero} -{" "}
                  {selectedEvent.codigo}
                </Typography>
                <Typography variant="body1">{selectedEvent.description}</Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <LabelIcon fontSize="small" color="primary" />
                  <Typography variant="h6" fontWeight="bold" color="primary.main">
                    Marca: {selectedEvent.marca}
                  </Typography>
                </Box>
                <Typography
                  variant="body1"
                  color="text.secondary"
                  sx={{ fontStyle: "italic", bgcolor: "#f5f5f5", p: 2, borderRadius: 1 }}
                >
                  {selectedEvent.significadoCodigo}
                </Typography>
                <Divider />
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Nome da Marca
                    </Typography>
                    <Typography fontWeight="bold" color="primary.main">
                      {selectedEvent.marca}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Número do Processo
                    </Typography>
                    <Typography fontWeight="bold">{selectedEvent.numero}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Código INPI
                    </Typography>
                    <Typography fontWeight="bold">{selectedEvent.codigo}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Despacho
                    </Typography>
                    <Typography>{selectedEvent.despacho}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Data de Publicação
                    </Typography>
                    <Typography>
                      {moment(selectedEvent.dataPublicacao).format("DD/MM/YYYY")}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Prazo
                    </Typography>
                    <Typography fontWeight="bold" color="error.main">
                      {selectedEvent.prazo}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Data de Vencimento
                    </Typography>
                    <Typography fontWeight="bold">
                      {moment(selectedEvent.prazoVencimento).format("DD/MM/YYYY")}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Cliente
                    </Typography>
                    <Typography>{selectedEvent.client}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Responsável
                    </Typography>
                    <Typography>{selectedEvent.responsible}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Local
                    </Typography>
                    <Typography>{selectedEvent.location}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Status
                    </Typography>
                    <Chip
                      label={statusLabels[selectedEvent.status]}
                      size="small"
                      sx={{ bgcolor: statusColors[selectedEvent.status], color: "white" }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Prioridade
                    </Typography>
                    <FormControl size="small" sx={{ minWidth: 120 }}>
                      <Select
                        value={editedPriority}
                        onChange={(e) => setEditedPriority(e.target.value)}
                        displayEmpty
                        renderValue={(value) => (
                          <Chip
                            label={value.toUpperCase()}
                            size="small"
                            sx={{
                              bgcolor: priorityColors[value],
                              color: "white",
                              minWidth: "auto",
                            }}
                          />
                        )}
                      >
                        {["alta", "media", "baixa"].map((p) => (
                          <MenuItem key={p} value={p}>
                            <Chip
                              label={p.toUpperCase()}
                              size="small"
                              sx={{ bgcolor: priorityColors[p], color: "white", minWidth: "auto" }}
                            />
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              </Stack>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancelar</Button>
            {selectedEvent && (
              <Button variant="contained" color="primary" onClick={handleSaveChanges}>
                Salvar Alterações
              </Button>
            )}
          </DialogActions>
        </Dialog>

        {/* Dialog — Eventos do dia */}
        <Dialog
          open={dayEventsDialog.open}
          onClose={handleCloseDayEventsDialog}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            Prazos INPI de {dayEventsDialog.date && dayEventsDialog.date.format("DD/MM/YYYY")}
          </DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              {dayEventsDialog.events.length === 0 ? (
                <Typography color="text.secondary" textAlign="center" sx={{ py: 4 }}>
                  Nenhum evento agendado para este dia
                </Typography>
              ) : (
                dayEventsDialog.events.map((event) => (
                  <Card
                    key={event.id}
                    variant="outlined"
                    sx={{
                      cursor: "pointer",
                      borderLeft: `4px solid ${eventTypeColors[event.type]}`,
                      "&:hover": { bgcolor: "#f5f5f5" },
                    }}
                    onClick={() => {
                      handleCloseDayEventsDialog();
                      handleEventClick(event);
                    }}
                  >
                    <CardContent>
                      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <Typography variant="h6" component="div">
                            {moment(event.date).format("DD/MM/YYYY")} -
                          </Typography>
                          <Typography
                            variant="h6"
                            component="span"
                            sx={{
                              color: "primary.main",
                              textDecoration: "underline",
                              fontWeight: "bold",
                              cursor: "pointer",
                              "&:hover": { color: "primary.dark" },
                            }}
                          >
                            {event.numero}
                          </Typography>
                          <Typography variant="h6" component="div">
                            - {event.codigo}
                          </Typography>
                        </Box>
                        <Box sx={{ display: "flex", gap: 1 }}>
                          <Chip
                            label={event.priority.toUpperCase()}
                            size="small"
                            sx={{ bgcolor: priorityColors[event.priority], color: "white" }}
                          />
                          <Chip
                            label={statusLabels[event.status]}
                            size="small"
                            sx={{ bgcolor: statusColors[event.status], color: "white" }}
                          />
                        </Box>
                      </Box>
                      <Typography color="text.secondary" gutterBottom>
                        {event.description}
                      </Typography>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 1 }}>
                        <LabelIcon fontSize="small" color="primary" />
                        <Typography variant="body2" fontWeight="bold" color="primary.main">
                          Marca: {event.marca}
                        </Typography>
                      </Box>
                      <Stack direction="row" spacing={2} alignItems="center">
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                          <PersonIcon fontSize="small" color="action" />
                          <Typography variant="body2">{event.responsible}</Typography>
                        </Box>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                          <BusinessIcon fontSize="small" color="action" />
                          <Typography variant="body2">{event.client}</Typography>
                        </Box>
                      </Stack>
                    </CardContent>
                  </Card>
                ))
              )}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDayEventsDialog}>Fechar</Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: "100%" }}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </JumboContentLayout>
  );
};

export default Agenda;
